/**
 * `updateLiveBucketFromAmm` — realtime patching of sparse event-time buckets on
 * `useGetMarketChart` from AMM swap events (`morfi.amm.${mint}.user.*.activity.v0`,
 * proto `AmmUserActivity`). The graduated/winner-page chart trades 100% via
 * the cp-swap AMM, so the bonding-curve `updateLiveBucket` never fires there;
 * this helper is the AMM-side equivalent.
 *
 * The AMM payload carries the post-reserve spot (`postOutcomePriceD9`) and
 * the execution average (`executionPriceD9`) for swaps, plus post-trade quote
 * reserves. Together with the indexed fixed outcome-mint supply captured in
 * the REST seed, that is enough to update price, market-cap, and reserve
 * charts without another REST fetch. This matches persisted chart semantics:
 * trade candles now source post-execution spot, while reserve candles use
 * post-trade reserves.
 * Values stay in the same raw D9/D6 scale as REST. The subject mint routes the
 * event to an outcome subscription; the committed payload market/outcome
 * identity then binds it to this exact chart cache before any mutation.
 *
 * `price` / `market_cap` patch ONLY genuine swaps: `executionPriceD9`
 * presence is the trade gate. LP, configuration and reconciliation
 * activities carry post state but never an execution price and create no
 * trade candle fact, so they no-op here instead of injecting chart points
 * absent from the server candles.
 *
 * The shared bucket-patch / rollover / structural-sharing logic lives in
 * `live-bucket-core.ts`. Returns the EXACT `prev` reference on every no-op.
 *
 * Platform-agnostic — pure data, no web/RN API. Safe in core.
 */

import { outcomeMarketCapAtPriceD6 } from '@morfi/market-math';
import type { PagedData } from '../types/PagedData.js';
import {
  applyBucketPatch,
  type ChartClock,
  type ChartQueryArgs,
  type ChartRow,
  readEventTsSeconds,
  readIntegerString,
  SYSTEM_CHART_CLOCK,
  throwChartRealtimeValueUnavailable,
} from './live-bucket-core.js';

function readPositiveIntegerString(raw: Record<string, unknown>, key: string): string | null {
  const value = readIntegerString(raw, key);
  if (value === null) return null;
  const parsed = BigInt(value);
  return parsed > 0n ? parsed.toString() : null;
}

function readOutcomeIndex(raw: Record<string, unknown>): number | null {
  const value = raw.outcomeIdx;
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : null;
}

/**
 * Apply one decoded `AmmUserActivity` swap event to a `useGetMarketChart`
 * cached envelope. Patches or inserts the event-time bucket, or bails.
 * Returns `prev` verbatim on no-op.
 */
export function updateLiveBucketFromAmm<Row extends ChartRow>(
  prev: PagedData<Row>,
  raw: Record<string, unknown>,
  args: ChartQueryArgs,
  clock: ChartClock = SYSTEM_CHART_CLOCK,
): PagedData<Row> {
  if (typeof args.market !== 'string' || args.market.length === 0 || raw.market !== args.market) {
    throwChartRealtimeValueUnavailable('amm_market_identity');
  }
  const expectedOutcomeIndex =
    typeof args.outcomeIndex === 'number' ? args.outcomeIndex : Number(args.outcomeIndex);
  if (
    !Number.isSafeInteger(expectedOutcomeIndex) ||
    expectedOutcomeIndex < 0 ||
    readOutcomeIndex(raw) !== expectedOutcomeIndex
  ) {
    throwChartRealtimeValueUnavailable('amm_outcome_identity');
  }

  let valueRaw: string | null;
  if (args.property === 'price') {
    // Trade gate: only swaps carry a positive execution price; LP/config/
    // reconcile activities carry post state but create no candle fact and
    // must not inject price points. New producers send both fields for
    // swaps; the price chart sources the post-reserve spot, never the
    // execution average.
    if (readPositiveIntegerString(raw, 'executionPriceD9') === null) return prev;
    valueRaw = readPositiveIntegerString(raw, 'postOutcomePriceD9');
    if (valueRaw === null) throwChartRealtimeValueUnavailable('amm_post_spot_price');
  } else if (args.property === 'reserves') {
    valueRaw = readPositiveIntegerString(raw, 'postQuoteReservesD6');
    if (valueRaw === null) throwChartRealtimeValueUnavailable('amm_post_quote_reserves');
  } else if (args.property === 'market_cap') {
    if (readPositiveIntegerString(raw, 'executionPriceD9') === null) return prev;
    const priceD9 = readPositiveIntegerString(raw, 'postOutcomePriceD9');
    const supplyD6 = prev.supplyD6;
    if (
      priceD9 === null ||
      supplyD6 === undefined ||
      !/^\d+$/.test(supplyD6) ||
      BigInt(supplyD6) <= 0n
    ) {
      throwChartRealtimeValueUnavailable('amm_market_cap_context');
    }
    const marketCap = outcomeMarketCapAtPriceD6({
      poolModel: 'cp_swap',
      priceD9: BigInt(priceD9),
      indexedOutcomeMintSupplyD6: BigInt(supplyD6),
    });
    if (!marketCap.available) throwChartRealtimeValueUnavailable('amm_market_cap_domain');
    valueRaw = marketCap.value.toString();
  } else {
    return prev;
  }

  const eventTs = readEventTsSeconds(raw);
  if (eventTs === null) {
    throwChartRealtimeValueUnavailable('amm_timestamp');
  }

  return applyBucketPatch(prev, args, valueRaw, eventTs, clock);
}
